import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { AccountSummary } from "../../shared/api-types";
import { apiError, useT } from "../i18n";
import { apiJson } from "../lib/api";
import { confirmLeave } from "../lib/leave-guard";
import { switchAccount } from "../lib/me";
import { Button, Input, Modal, Popover, popoverItem, popoverLabel } from "./ui";

/**
 * Aktiver Account in der Kopfzeile. Mit mehreren Mitgliedschaften ein Umschalter;
 * Betreiber haben darin zusätzlich „Neuer Account“ (Ersteller wird sofort Admin).
 */
export function AccountSwitcher({
  active,
  accounts,
  canCreate,
  fullWidth = false,
}: {
  active: AccountSummary | null;
  accounts: AccountSummary[];
  canCreate: boolean;
  /** In der Seitenleiste über die volle Breite statt am Text entlang */
  fullWidth?: boolean;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  async function create(e: FormEvent) {
    e.preventDefault();
    // Anlegen hängt die Sitzung sofort an den neuen Account — laufende Uploads vorher klären
    if (!(await confirmLeave())) return;
    setBusy(true);
    setError(null);
    try {
      await apiJson("/api/accounts", { method: "POST", json: { name: name.trim() } });
      // Sitzung hängt jetzt am neuen Account → komplett neu laden
      window.location.assign("/");
    } catch (err) {
      setError(apiError(t, err instanceof Error ? err.message : "unknown"));
      setBusy(false);
    }
  }

  const label = active?.name ?? t.app.noAccountTitle;

  if (accounts.length < 2 && !canCreate) {
    return (
      <span
        className={
          fullWidth
            ? "block truncate px-2 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300"
            : "truncate text-sm text-zinc-500 dark:text-zinc-400"
        }
        title={label}
      >
        {label}
      </span>
    );
  }

  return (
    <>
      <Popover
        role="listbox"
        align="start"
        width="w-64"
        title={t.app.switchAccount}
        sheetTitle={t.app.switchAccount}
        triggerClassName={`flex min-w-0 items-center gap-1.5 rounded-lg text-sm text-zinc-700 transition-colors hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800 ${
          fullWidth ? "w-full justify-between px-2 py-2 font-medium" : "max-w-56 px-2 py-1"
        }`}
        trigger={() => (
          <>
            <span className="truncate">{label}</span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-zinc-400" />
          </>
        )}
      >
        {(close) => (
          <>
            {/* Im Bottom-Sheet steht die Überschrift schon oben */}
            {accounts.length > 0 && (
              <p className={`${popoverLabel} hidden sm:block`}>{t.app.switchAccount}</p>
            )}
            {accounts.map((a) => (
              <button
                key={a.id}
                role="option"
                aria-selected={a.id === active?.id}
                onClick={() => {
                  close();
                  if (a.id !== active?.id) void switchAccount(a.id);
                }}
                className={popoverItem}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{a.name}</span>
                  <span className="block text-xs text-zinc-400">
                    {a.role === "owner" ? t.members.roleOwner : t.members.roleMember}
                  </span>
                </span>
                {a.id === active?.id && <Check className="size-4 shrink-0 text-accent" />}
              </button>
            ))}
            {canCreate && (
              <div
                className={
                  accounts.length > 0
                    ? "mt-1 border-t border-zinc-100 pt-1 dark:border-zinc-800"
                    : ""
                }
              >
                <button
                  onClick={() => {
                    close();
                    setCreateOpen(true);
                  }}
                  className={popoverItem}
                >
                  <Plus className="size-4 text-zinc-400" />
                  {t.app.newAccount}
                </button>
              </div>
            )}
          </>
        )}
      </Popover>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t.app.newAccountTitle}>
        <form onSubmit={(e) => void create(e)} className="space-y-4">
          <Input
            className="w-full"
            placeholder={t.app.newAccountPlaceholder}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button type="submit" disabled={busy}>
              {t.common.create}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
